import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ContentRenderer, exportToDocx } from './ResearchDashboard';
import type { CostData, ContentBrief } from './ResearchDashboard';

// ═══════════════════════════════════════
// COLOR SYSTEM
// ═══════════════════════════════════════
const C = {
  bg: "#0c0c0e", panelL: "#0c0c0e", panelR: "#18181b",
  surface: "#141416", card: "#1a1a1e", raised: "#222226", hover: "#2a2a30",
  border: "rgba(255,255,255,0.06)", borderMd: "rgba(255,255,255,0.10)", borderHi: "rgba(255,255,255,0.16)",
  text: "#e4e4e8", t2: "#a1a1ab", t3: "#71717b", t4: "#3f3f46",
  vi: "#8b5cf6", viDim: "#7c3aed", viSoft: "rgba(139,92,246,0.12)", viGlow: "rgba(139,92,246,0.06)",
  gn: "#34d399", gnSoft: "rgba(52,211,153,0.10)", rd: "#ef4444",
};
const mono = `'JetBrains Mono','SF Mono','Fira Code',monospace`;

// ═══════════════════════════════════════
// TYPES
// ═══════════════════════════════════════
interface Client { id: string; name: string; }
interface Framework { id: string; framework_name: string; system_prompt: string; }
interface Knowledge { id: string; title: string; file_name: string | null; token_estimate: number | null; }
interface ActivityMsg { id: number; t: 'search' | 'think' | 'status'; text: string; active: boolean; }

type Phase = 0 | 1 | 2;
const PH = { form: 0 as Phase, gen: 1 as Phase, done: 2 as Phase };

// ═══════════════════════════════════════
// MICRO COMPONENTS
// ═══════════════════════════════════════
const Dot: React.FC<{ color: string; pulse?: boolean }> = ({ color, pulse }) => (
  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, display: 'inline-block', animation: pulse ? 'clPulse 1.2s ease infinite' : 'none' }} />
);

const Pill: React.FC<{ children: React.ReactNode; variant?: 'accent' | 'green' }> = ({ children, variant }) => (
  <span style={{
    display: 'inline-flex', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600,
    backgroundColor: variant === 'accent' ? C.viSoft : variant === 'green' ? C.gnSoft : 'rgba(255,255,255,0.05)',
    color: variant === 'accent' ? C.vi : variant === 'green' ? C.gn : C.t3,
  }}>{children}</span>
);

const TypingDots: React.FC = () => (
  <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 16 }}>
    {[0, 1, 2].map(i => <span key={i} style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: C.t3, animation: `clDotPulse 1.4s ease-in-out ${i * 0.16}s infinite` }} />)}
  </div>
);

const SmBtn: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button onClick={onClick} style={{
    padding: '4px 8px', borderRadius: 5, border: `1px solid ${C.border}`, backgroundColor: 'transparent',
    color: C.t3, fontSize: 10, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  }}
    onMouseEnter={e => (e.currentTarget.style.backgroundColor = C.hover)}
    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
  >{label}</button>
);

// ═══════════════════════════════════════
// ACTIVITY MESSAGE BUBBLE
// ═══════════════════════════════════════
const MsgBubble: React.FC<{ msg: ActivityMsg }> = ({ msg }) => {
  if (msg.t === 'status') return (
    <div style={{ padding: '10px 0 4px', animation: 'clSlideIn 0.25s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ height: 1, flex: 1, background: `linear-gradient(to right, ${C.border}, transparent)` }} />
        <span style={{ fontSize: 9, fontWeight: 700, color: C.t4, letterSpacing: '0.06em', textTransform: 'uppercase' as const }}>{msg.text}</span>
        <div style={{ height: 1, flex: 1, background: `linear-gradient(to left, ${C.border}, transparent)` }} />
      </div>
    </div>
  );
  const isSearch = msg.t === 'search';
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 10, margin: '1px 0',
      backgroundColor: msg.active ? C.surface : 'transparent',
      border: `1px solid ${msg.active ? C.border : 'transparent'}`,
      animation: 'clSlideIn 0.25s ease', transition: 'all 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          backgroundColor: msg.active ? (isSearch ? 'rgba(59,130,246,0.12)' : C.viSoft) : 'transparent',
          display: 'grid', placeItems: 'center', transition: 'background 0.3s',
        }}>
          {isSearch ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={msg.active ? '#93bbfd' : C.t4} strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={msg.active ? '#a78bfa' : C.t4} strokeWidth="2.5" strokeLinecap="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12" /></svg>
          )}
        </div>
        <div style={{ flex: 1, paddingTop: 1 }}>
          {isSearch ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: msg.active ? '#60a5fa' : C.t4, letterSpacing: '0.04em' }}>SEARCH</span>
              <span style={{ fontSize: 11, color: msg.active ? C.t2 : C.t3, fontFamily: mono }}>{msg.text}</span>
            </div>
          ) : (
            <span style={{ fontSize: 12, color: msg.active ? C.t2 : C.t3, lineHeight: 1.5 }}>{msg.text}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// FIELD WRAPPER
// ═══════════════════════════════════════
const Field: React.FC<{ n: number; label: string; right?: string; last?: boolean; children: React.ReactNode }> = ({ n, label, right, last, children }) => (
  <div style={{ marginBottom: last ? 0 : 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: C.viSoft, display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: C.vi }}>{n}</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: C.t3, letterSpacing: '0.05em' }}>{label}</span>
      </div>
      {right && <span style={{ fontSize: 10, color: C.t4 }}>{right}</span>}
    </div>
    {children}
  </div>
);

// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════
const GenerateTOC: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>(PH.form);

  // Data state
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedFramework, setSelectedFramework] = useState('');
  const [knowledgeFiles, setKnowledgeFiles] = useState<Knowledge[]>([]);
  const [selectedKnowledge, setSelectedKnowledge] = useState<Set<string>>(new Set());
  const [focusKeyword, setFocusKeyword] = useState('');

  // Generation state
  const [rawMarkdown, setRawMarkdown] = useState(''); // For DB storage
  const [structuredContent, setStructuredContent] = useState<ContentBrief | null>(null); // Parsed JSON
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [savedTocId, setSavedTocId] = useState<string | null>(null);
  const [costData, setCostData] = useState<CostData | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [searchCount, setSearchCount] = useState(0);
  const [editCount, setEditCount] = useState(0);

  // Activity messages
  const [activityMsgs, setActivityMsgs] = useState<ActivityMsg[]>([]);

  const startRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const streamEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedClientObj = clients.find(c => c.id === selectedClient);
  const selectedFw = frameworks.find(f => f.id === selectedFramework);
  const canGenerate = selectedClient && selectedFramework && focusKeyword.trim();

  const totalTokens = useMemo(() => {
    return knowledgeFiles.filter(k => selectedKnowledge.has(k.id)).reduce((sum, k) => sum + (k.token_estimate || 0), 0);
  }, [knowledgeFiles, selectedKnowledge]);

  // ─── Data loading ───
  useEffect(() => {
    supabase.from('content_lab_clients').select('id, name').order('name').then(({ data }) => {
      setClients(data || []);
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setFrameworks([]); setKnowledgeFiles([]); setSelectedFramework(''); setSelectedKnowledge(new Set());
      return;
    }
    Promise.all([
      supabase.from('content_lab_frameworks').select('id, framework_name, system_prompt').eq('client_id', selectedClient),
      supabase.from('content_lab_knowledge').select('id, title, file_name, token_estimate').eq('client_id', selectedClient),
    ]).then(([fwRes, knRes]) => {
      const fws = fwRes.data || [];
      setFrameworks(fws);
      setKnowledgeFiles(knRes.data || []);
      setSelectedKnowledge(new Set((knRes.data || []).map(k => k.id)));
      if (fws.length === 1) setSelectedFramework(fws[0].id);
      else setSelectedFramework('');
    });
  }, [selectedClient]);

  // ─── Timer ───
  useEffect(() => {
    if (phase === PH.gen) {
      startRef.current = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 200);
    } else if (phase === PH.done && timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase]);

  useEffect(() => { streamEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [activityMsgs]);
  useEffect(() => { if (phase === PH.form) setTimeout(() => inputRef.current?.focus(), 100); }, [phase]);

  // ─── Keyboard shortcut ───
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && phase === PH.form && canGenerate && !generating) {
        e.preventDefault();
        handleGenerate();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, generating, canGenerate, selectedClient, selectedFramework, focusKeyword, selectedKnowledge]);

  // ─── Add activity message helper (with Set-based deduplication) ───
  const seenActivityRef = useRef<Set<string>>(new Set());
  const addActivity = useCallback((msg: { type: string; text: string }) => {
    // Skip if we've EVER seen this exact message in this generation
    const key = `${msg.type}:${msg.text}`;
    if (seenActivityRef.current.has(key)) return;
    seenActivityRef.current.add(key);

    setActivityMsgs(prev => {
      const deactivated = prev.map(m => ({ ...m, active: false }));
      return [...deactivated, {
        id: Date.now() + Math.random(),
        t: msg.type as ActivityMsg['t'],
        text: msg.text,
        active: true,
      }];
    });
    if (msg.type === 'search') {
      setSearchCount(c => c + 1);
    }
  }, []);

  // ─── Generate ───
  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return;
    setPhase(PH.gen);
    setRawMarkdown(''); setStructuredContent(null); setVisibleSections(new Set());
    setSavedTocId(null); setCostData(null); setElapsed(0); setSearchCount(0); setEditCount(0);
    setActivityMsgs([]);
    seenActivityRef.current.clear();
    setGenerating(true);

    try {
      const response = await fetch(
        `https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/generate-toc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo',
          },
          body: JSON.stringify({
            client_id: selectedClient,
            framework_id: selectedFramework,
            focus_keyword: focusKeyword.trim(),
            selected_knowledge_ids: Array.from(selectedKnowledge),
          }),
        }
      );

      if (!response.ok) throw new Error(await response.text() || 'Generation failed');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No stream available');

      const decoder = new TextDecoder();
      let buffer = '';
      let parsedCost: CostData | null = null;
      let currentEventType = '';
      let storedRawMarkdown = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);

          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }

          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);

            if (currentEventType === 'activity') {
              addActivity(parsed);
              currentEventType = '';
              continue;
            }

            if (currentEventType === 'content') {
              // Structured section data from server
              const { section, data } = parsed;
              setStructuredContent(prev => ({
                ...(prev || { brief: {}, outline: [], refs: [], keywords: [], internalLinks: [], externalGuidelines: [] }),
                ...(section === 'brief' ? { brief: data } : {}),
                ...(section === 'outline' ? { outline: data } : {}),
                ...(section === 'refs' ? { refs: data } : {}),
                ...(section === 'links' ? { internalLinks: data.internal || [], externalGuidelines: data.guidelines || [] } : {}),
              }));
              setVisibleSections(prev => new Set([...prev, section]));
              currentEventType = '';
              continue;
            }

            if (currentEventType === 'raw') {
              storedRawMarkdown = parsed.markdown || '';
              setRawMarkdown(storedRawMarkdown);
              currentEventType = '';
              continue;
            }

            if (currentEventType === 'cost') {
              parsedCost = parsed as CostData;
              setCostData(parsedCost);
              currentEventType = '';
              continue;
            }

            if (currentEventType === 'done') {
              currentEventType = '';
              continue;
            }

            currentEventType = '';
          } catch { /* partial JSON */ }
        }
      }

      // Flush remaining buffer
      if (buffer.trim()) {
        for (const raw of buffer.split('\n')) {
          if (!raw || raw.startsWith('event:')) continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.input_tokens !== undefined && parsed.total_cost !== undefined) {
              parsedCost = parsed as CostData;
              setCostData(parsedCost);
            }
          } catch {}
        }
      }

      if (timerRef.current) clearInterval(timerRef.current);
      const finalElapsed = Math.round((Date.now() - startRef.current) / 1000);
      setElapsed(finalElapsed);

      setActivityMsgs(prev => prev.map(m => ({ ...m, active: false })));
      setPhase(PH.done);

      // Save to DB
      const { data: { user } } = await supabase.auth.getUser();
      if (user && storedRawMarkdown) {
        const { data, error } = await supabase.from('content_lab_tocs').insert({
          client_id: selectedClient, framework_id: selectedFramework, focus_keyword: focusKeyword.trim(),
          output_content: storedRawMarkdown, created_by: user.id, generation_time_seconds: finalElapsed,
          input_tokens: parsedCost?.input_tokens ?? null, output_tokens: parsedCost?.output_tokens ?? null,
          search_count: parsedCost?.searches ?? null,
          generation_cost: parsedCost ? parseFloat(parsedCost.total_cost.replace('$', '')) : null,
        }).select('id').single();
        if (!error && data) { setSavedTocId(data.id); toast.success('TOC saved'); }
      }
    } catch (err: any) {
      if (timerRef.current) clearInterval(timerRef.current);
      toast.error(err.message || 'Generation failed');
      setPhase(PH.form);
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, selectedClient, selectedFramework, focusKeyword, selectedKnowledge, addActivity]);

  const reset = () => {
    setPhase(PH.form);
    setRawMarkdown(''); setStructuredContent(null); setVisibleSections(new Set());
    setSavedTocId(null); setCostData(null); setElapsed(0); setSearchCount(0); setEditCount(0);
    setActivityMsgs([]);
  };

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(rawMarkdown);
    toast.success('Copied to clipboard');
  }, [rawMarkdown]);

  const handleInlineEdit = useCallback((path: string, value: string) => {
    setEditCount(c => c + 1);
    toast.success('Edit saved', { duration: 1500 });
    console.log(`Inline edit: ${path} → ${value}`);
  }, []);

  const isDone = phase === PH.done;
  const hasContent = structuredContent && visibleSections.size > 0;

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: C.bg, color: C.text, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif", WebkitFontSmoothing: 'antialiased' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        @keyframes clFadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes clFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes clSlideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
        @keyframes clPulse{0%,100%{opacity:.35}50%{opacity:1}}
        @keyframes clDotPulse{0%,80%,100%{transform:scale(0);opacity:0}40%{transform:scale(1);opacity:1}}
        .cl-root ::-webkit-scrollbar{width:5px}
        .cl-root ::-webkit-scrollbar-track{background:transparent}
        .cl-root ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px}
        .cl-root input::placeholder{color:${C.t4}}
        .cl-root input:focus{outline:none}
      `}</style>

      {/* ═══ TOP BAR ═══ */}
      <header style={{ height: 48, flexShrink: 0, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10, backgroundColor: C.surface }}>
        <div
          style={{ width: 28, height: 28, borderRadius: 6, display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.t3 }}
          onClick={() => navigate('/content-lab')}
          title="Back to Content Lab"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </div>
        <div
          style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg,${C.vi},${C.viDim})`, display: 'grid', placeItems: 'center' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /></svg>
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/content-lab')}>Content Lab</span>
        {phase >= PH.gen && (
          <>
            <span style={{ color: C.t4 }}>/</span>
            <span style={{ fontSize: 13, color: C.t3 }}>{focusKeyword}</span>
          </>
        )}
        <div style={{ flex: 1 }} />
        {isDone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, color: C.t4 }}>Click text to edit</span>
            {editCount > 0 && <Pill variant="accent">{editCount} edit{editCount !== 1 ? 's' : ''}</Pill>}
          </div>
        )}
        {phase >= PH.gen && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
            {phase === PH.gen
              ? <><Dot color={C.vi} pulse /><span style={{ fontSize: 11, color: C.vi, fontWeight: 500 }}>{elapsed}s</span></>
              : <><Dot color={C.gn} /><span style={{ fontSize: 11, color: C.gn, fontWeight: 500 }}>Done · {elapsed}s</span></>}
          </div>
        )}
      </header>

      {/* ═══ MAIN AREA ═══ */}
      <div className="cl-root" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ════ LEFT PANEL ════ */}
        <div style={{
          width: phase === PH.form ? '100%' : 420,
          minWidth: phase === PH.form ? 0 : 360,
          maxWidth: phase === PH.form ? 540 : 420,
          margin: phase === PH.form ? '0 auto' : 0,
          display: 'flex', flexDirection: 'column',
          borderRight: phase >= PH.gen ? `1px solid ${C.border}` : 'none',
          backgroundColor: C.panelL,
          transition: 'all 0.35s cubic-bezier(.4,0,.2,1)',
        }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: phase === PH.form ? '0 24px' : '0 16px' }}>
            <div style={{ paddingTop: phase === PH.form ? 80 : 16, paddingBottom: 20, animation: 'clFadeUp 0.4s ease' }}>

              {/* Form header */}
              {phase === PH.form && (
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em' }}>Generate Content Brief</h1>
                  <p style={{ fontSize: 13, color: C.t3, marginTop: 4 }}>Research competitors, build outlines, map keywords</p>
                </div>
              )}

              {/* Form card */}
              <div style={{
                backgroundColor: phase === PH.form ? C.surface : 'transparent',
                border: phase === PH.form ? `1px solid ${C.border}` : 'none',
                borderRadius: 14, padding: phase === PH.form ? '20px 20px 16px' : '0',
              }}>
                {/* 1: Client */}
                <Field n={1} label="CLIENT">
                  <Select value={selectedClient} onValueChange={v => { setSelectedClient(v); setFocusKeyword(''); }}>
                    <SelectTrigger className="border-0 h-auto" style={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 11px', fontSize: 13, color: C.text }}>
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: C.card, border: `1px solid ${C.borderMd}`, color: C.text }}>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>

                {/* 2: Framework */}
                {selectedClient && (
                  <Field n={2} label="FRAMEWORK">
                    <Select value={selectedFramework} onValueChange={setSelectedFramework}>
                      <SelectTrigger className="border-0 h-auto" style={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 11px', fontSize: 13, color: C.text }}>
                        <SelectValue placeholder="Select framework..." />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: C.card, border: `1px solid ${C.borderMd}`, color: C.text }}>
                        {frameworks.map(f => <SelectItem key={f.id} value={f.id}>{f.framework_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {selectedFw && <p style={{ fontSize: 10, color: C.t4, marginTop: 4, paddingLeft: 2 }}>{selectedFw.system_prompt.slice(0, 80)}…</p>}
                    {frameworks.length === 0 && <p style={{ fontSize: 10, color: C.rd, marginTop: 4 }}>No frameworks found.</p>}
                  </Field>
                )}

                {/* 3: Knowledge */}
                {selectedFramework && knowledgeFiles.length > 0 && (
                  <Field n={3} label="KNOWLEDGE" right={`~${Math.round(totalTokens / 1000)}k tokens`}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {knowledgeFiles.map(kn => (
                        <label key={kn.id} style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
                          <Checkbox
                            checked={selectedKnowledge.has(kn.id)}
                            onCheckedChange={checked => {
                              const next = new Set(selectedKnowledge);
                              if (checked) next.add(kn.id); else next.delete(kn.id);
                              setSelectedKnowledge(next);
                            }}
                            className="border-white/20 data-[state=checked]:bg-[#8b5cf6] data-[state=checked]:border-[#8b5cf6]"
                          />
                          <span style={{ fontSize: 11, color: C.t2, flex: 1 }}>{kn.title}</span>
                          <Pill>{Math.round((kn.token_estimate || 0) / 1000)}k</Pill>
                        </label>
                      ))}
                    </div>
                  </Field>
                )}

                {/* 4: Focus Keyword */}
                {selectedFramework && (
                  <Field n={4} label="FOCUS KEYWORD" last>
                    <input
                      ref={inputRef}
                      value={focusKeyword}
                      onChange={e => setFocusKeyword(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canGenerate) handleGenerate(); }}
                      placeholder="e.g. freight quoting best practices"
                      style={{
                        width: '100%', backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
                        padding: '9px 11px', fontSize: 13, color: C.text, fontFamily: 'inherit',
                      }}
                    />
                  </Field>
                )}

                {/* Summary + Generate (form only) */}
                {phase === PH.form && canGenerate && (
                  <>
                    <div style={{ height: 1, background: C.border, margin: '16px 0' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', marginBottom: 16 }}>
                      {[
                        ['Client', selectedClientObj?.name || '—'],
                        ['Framework', selectedFw?.framework_name || '—'],
                        ['Knowledge', `${selectedKnowledge.size} file${selectedKnowledge.size !== 1 ? 's' : ''}`],
                        ['Keyword', focusKeyword || '—'],
                      ].map(([l, v]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                          <span style={{ fontSize: 10, color: C.t4 }}>{l}</span>
                          <span style={{ fontSize: 10, color: C.t3, fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleGenerate}
                      disabled={!canGenerate || generating}
                      style={{
                        width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                        backgroundColor: canGenerate ? C.vi : C.raised,
                        color: canGenerate ? '#fff' : C.t4,
                        fontSize: 13, fontWeight: 600, cursor: canGenerate ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                      }}
                    >Generate</button>
                    <p style={{ textAlign: 'center', fontSize: 10, color: C.t4, marginTop: 6 }}>⌘+Enter</p>
                  </>
                )}
              </div>
            </div>

            {/* Activity stream (gen/done phases) */}
            {phase >= PH.gen && (
              <div style={{ paddingBottom: 20 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
                  <Pill variant="accent">{selectedClientObj?.name}</Pill>
                  <Pill>{selectedFw?.framework_name}</Pill>
                  <Pill>{selectedKnowledge.size} knowledge file{selectedKnowledge.size !== 1 ? 's' : ''}</Pill>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {activityMsgs.map(m => <MsgBubble key={m.id} msg={m} />)}
                  {phase === PH.gen && <div style={{ padding: '8px 12px' }}><TypingDots /></div>}
                  <div ref={streamEndRef} />
                </div>

                {/* Completion stats */}
                {isDone && (
                  <div style={{ marginTop: 16, animation: 'clFadeUp 0.3s ease' }}>
                    <div style={{ backgroundColor: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <Dot color={C.gn} /><span style={{ fontSize: 11, fontWeight: 600, color: C.gn }}>Research complete</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        {[
                          ['Searches', costData?.searches ?? searchCount],
                          ['Tokens', costData ? `${Math.round((costData.input_tokens + costData.output_tokens) / 1000)}K` : '—'],
                          ['Cost', costData?.total_cost ?? '—'],
                        ].map(([l, v]) => (
                          <div key={l as string} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 16, fontWeight: 600, fontFamily: mono, color: C.text }}>{v}</div>
                            <div style={{ fontSize: 9, color: C.t4, fontWeight: 600, letterSpacing: '0.04em' }}>{(l as string).toUpperCase()}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button onClick={reset} style={{
                      width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 8,
                      border: `1px solid ${C.border}`, backgroundColor: 'transparent',
                      color: C.t3, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    }}>← New generation</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ════ RIGHT PANEL ════ */}
        {phase >= PH.gen && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: C.panelR, animation: 'clFadeIn 0.4s ease' }}>
            {/* Right header */}
            <div style={{
              height: 44, flexShrink: 0, borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.t3} strokeWidth="2" strokeLinecap="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /></svg>
                <span style={{ fontSize: 12, fontWeight: 500, color: C.t2 }}>Content Brief</span>
                {phase === PH.gen && <Pill variant="accent">Generating</Pill>}
                {isDone && <Pill variant="green">Complete</Pill>}
              </div>
              {isDone && (
                <div style={{ display: 'flex', gap: 3 }}>
                  <SmBtn label="Copy" onClick={handleCopy} />
                  <SmBtn label="DOCX" onClick={async () => {
                    await exportToDocx(rawMarkdown, focusKeyword);
                    toast.success('DOCX downloaded');
                  }} />
                  <SmBtn label="Redo" onClick={() => { if (window.confirm('Regenerate? Current content will be lost.')) handleGenerate(); }} />
                </div>
              )}
            </div>

            {/* Right content — renders structured JSON data */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 32px 60px' }}>
                {!hasContent && phase === PH.gen && (
                  <div style={{ textAlign: 'center', padding: '80px 0' }}>
                    <TypingDots />
                    <p style={{ fontSize: 12, color: C.t4, marginTop: 12 }}>Researching and generating...</p>
                  </div>
                )}

                {hasContent && structuredContent && (
                  <StructuredContentView
                    content={structuredContent}
                    visibleSections={visibleSections}
                    onEdit={handleInlineEdit}
                    disabled={phase !== PH.done}
                    articleContext={{
                      keyword: focusKeyword,
                      title: structuredContent?.brief?.title,
                      tone: structuredContent?.brief?.tone,
                      goal: structuredContent?.brief?.goal,
                      client: selectedClientObj?.name,
                    }}
                  />
                )}

                {phase === PH.gen && hasContent && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                    <TypingDots /><span style={{ fontSize: 11, color: C.t4 }}>Loading next section...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// STRUCTURED CONTENT VIEW — renders parsed JSON directly
// ═══════════════════════════════════════
const StructuredContentView: React.FC<{
  content: ContentBrief;
  visibleSections: Set<string>;
  onEdit: (path: string, value: string) => void;
  disabled: boolean;
  articleContext?: { keyword?: string; title?: string; tone?: string; goal?: string; client?: string };
}> = ({ content, visibleSections, onEdit, disabled, articleContext }) => {
  const { Editable } = React.useMemo(() => ({ Editable: EditableInline }), []);

  const sectionHeader = (num: number, title: string) => (
    <div style={{ marginTop: num === 1 ? 0 : 32, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ width: 18, height: 18, borderRadius: 5, backgroundColor: 'rgba(139,92,246,0.12)', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#8b5cf6', fontFamily: "'JetBrains Mono', monospace" }}>
          {num}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e8' }}>{title}</span>
      </div>
    </div>
  );

  const wrap = (text: string, path: string, editable: boolean) => {
    if (!disabled && editable) {
      return <Editable value={text} path={path} onChange={(v) => onEdit(path, v)} disabled={disabled} articleContext={articleContext} />;
    }
    return <span>{text}</span>;
  };

  return (
    <div style={{ animation: 'clFadeUp 0.3s ease' }}>
      {/* Section 1: Brief */}
      {visibleSections.has('brief') && Object.keys(content.brief).length > 0 && (
        <div>
          {sectionHeader(1, 'Article Brief')}
          <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
            {Object.entries(content.brief).map(([key, value], ri) => {
              const labels: Record<string, string> = {
                keyword: 'Primary keyword', title: 'Topic / Working title', wordCount: 'Avg competitor word count',
                slug: 'URL slug', type: 'Content type', tone: 'Tone', goal: 'Goal of the article',
                enhancement: 'Article enhancement', depth: 'Article depth',
              };
              const label = labels[key] || key;
              return (
                <div key={key} style={{
                  display: 'grid', gridTemplateColumns: '140px 1fr',
                  backgroundColor: ri % 2 === 0 ? '#1a1a1e' : '#141416',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}>
                  <div style={{ padding: '7px 12px', fontSize: 11, fontWeight: 600, color: '#71717b' }}>{label}</div>
                  <div style={{ padding: '6px 10px', fontSize: 12, color: '#e4e4e8' }}>
                    {wrap(value, `brief.${key}`, true)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 2: Outline */}
      {visibleSections.has('outline') && content.outline.length > 0 && (
        <div>
          {sectionHeader(2, 'Suggested Outline')}
          {content.outline.map((item, i) => {
            if (item.t === 'h1') return (
              <div key={i} style={{ fontSize: 15, fontWeight: 600, color: '#e4e4e8', marginBottom: 14, lineHeight: 1.3, marginTop: i > 0 ? 20 : 0 }}>
                {wrap(item.text, `outline.${i}.text`, true)}
              </div>
            );
            if (item.t === 'h2') return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, marginBottom: 3 }}>
                <span style={{ width: 5, height: 5, borderRadius: 2, backgroundColor: '#8b5cf6', flexShrink: 0, marginTop: 7 }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#e4e4e8' }}>
                  {wrap(item.text, `outline.${i}.text`, true)}
                </div>
                {item.up && (
                  <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, backgroundColor: 'rgba(139,92,246,0.12)', color: '#8b5cf6', flexShrink: 0, whiteSpace: 'nowrap' }}>UP</span>
                )}
              </div>
            );
            if (item.t === 'h3') return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 6, marginBottom: 2, paddingLeft: 18 }}>
                <span style={{ width: 3, height: 3, borderRadius: 1, backgroundColor: '#3f3f46', flexShrink: 0, marginTop: 7 }} />
                <div style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#a1a1ab' }}>
                  {wrap(item.text, `outline.${i}.text`, true)}
                </div>
                {item.up && (
                  <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, backgroundColor: 'rgba(139,92,246,0.12)', color: '#8b5cf6', flexShrink: 0, whiteSpace: 'nowrap' }}>UP</span>
                )}
              </div>
            );
            if (item.t === 'p' && item.items) return (
              <div key={i}>
                {item.items.map((bullet, bi) => (
                  <div key={bi} style={{ paddingLeft: 26, fontSize: 11, color: '#71717b', padding: '1px 0', display: 'flex', gap: 6 }}>
                    <span style={{ color: '#3f3f46', flexShrink: 0 }}>–</span>
                    <span>{wrap(bullet, `outline.${i}.items.${bi}`, true)}</span>
                  </div>
                ))}
              </div>
            );
            return null;
          })}
        </div>
      )}

      {/* Section 3: SERP References */}
      {visibleSections.has('refs') && content.refs.length > 0 && (
        <div>
          {sectionHeader(3, 'SERP References')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {content.refs.map((ref, i) => {
              const href = ref.url || `https://${ref.domain}`;
              return (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2.5 no-underline"
                  style={{
                    padding: '8px 10px', backgroundColor: '#1a1a1e', borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.06)', transition: 'all 0.15s',
                    textDecoration: 'none', color: 'inherit', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.backgroundColor = '#222226'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.backgroundColor = '#1a1a1e'; }}
                >
                  <span style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: 'rgba(139,92,246,0.12)', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#8b5cf6', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                    {ref.p}
                  </span>
                  <span style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: '#222226', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#a1a1ab', flexShrink: 0, textTransform: 'uppercase' }}>
                    {ref.domain?.[0] || '?'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 500, color: '#e4e4e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ref.title}
                  </div>
                  <span style={{ fontSize: 9, color: '#3f3f46', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                    {ref.domain}
                  </span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#3f3f46" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/>
                  </svg>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Section 4: Links */}
      {visibleSections.has('links') && (content.internalLinks.length > 0 || content.externalGuidelines.length > 0) && (
        <div>
          {sectionHeader(4, 'Internal / External Links')}
          {content.internalLinks.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-white/[0.05] mb-4">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-purple-600/10">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-purple-300/70">URL</th>
                    <th className="px-4 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-purple-300/70">Suggested Anchor</th>
                  </tr>
                </thead>
                <tbody>
                  {content.internalLinks.map((link, i) => (
                    <tr key={i} className={`border-b border-white/[0.03] last:border-b-0 ${i % 2 === 0 ? 'bg-[#12121a]' : 'bg-[#1a1a2e]'}`}>
                      <td className="px-4 py-2 font-mono text-xs text-[#71717a]">{link.url}</td>
                      <td className="px-4 py-2 text-[#f0f0f5]/85">{link.anchor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {content.externalGuidelines.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[#71717a] uppercase tracking-wider mb-2">External Link Guidelines</h4>
              {content.externalGuidelines.map((g, i) => (
                <div key={i} className="text-sm text-[#f0f0f5]/85 pl-5 py-0.5 leading-7 flex items-start gap-2">
                  <span className="text-[#71717a] mt-1 shrink-0">•</span>
                  <span>{g}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
// INLINE EDITABLE (used in StructuredContentView)
// ═══════════════════════════════════════
function EditableInline({ value, path, onChange, disabled, articleContext }: {
  value: string; path: string; onChange: (v: string) => void; disabled?: boolean;
  articleContext?: { keyword?: string; title?: string; tone?: string; goal?: string; client?: string };
}) {
  const [mode, setMode] = useState<'idle' | 'editing' | 'ai'>('idle');
  const [localValue, setLocalValue] = useState(value);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const aiInputRef = useRef<HTMLInputElement>(null);
  const originalValue = useRef(value);

  useEffect(() => { setLocalValue(value); }, [value]);

  const startEdit = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    originalValue.current = value;
    setMode('editing');
    setLocalValue(value);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const save = useCallback(() => {
    const trimmed = localValue.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    setMode('idle');
    setAiPrompt('');
    setAiLoading(false);
  }, [localValue, value, onChange]);

  const cancel = useCallback(() => {
    setLocalValue(originalValue.current);
    setMode('idle');
    setAiPrompt('');
    setAiLoading(false);
  }, []);

  const switchToAI = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMode('ai');
    setAiPrompt('');
    setTimeout(() => aiInputRef.current?.focus(), 30);
  };

  const switchToEdit = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMode('editing');
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const handleAISubmit = async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/ai-rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}`, 'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo' },
        body: JSON.stringify({ original: localValue, instruction: aiPrompt, context: path, articleContext }),
      });
      const data = await res.json();
      if (data.rewritten) {
        setLocalValue(data.rewritten);
        onChange(data.rewritten);
        setMode('idle');
        setAiPrompt('');
      }
    } catch (err) {
      console.error('Rewrite failed:', err);
    }
    setAiLoading(false);
  };

  useEffect(() => {
    if (mode === 'idle') return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (mode === 'editing') save();
        else cancel();
      }
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 10);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [mode, save, cancel]);

  if (mode === 'idle') {
    return (
      <span
        onClick={startEdit}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          borderRadius: 3, padding: '1px 3px', margin: '-1px -3px',
          transition: 'all 0.15s', display: 'inline',
          borderBottom: disabled ? 'none' : '1px dashed transparent',
        }}
        onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; e.currentTarget.style.borderBottomColor = 'rgba(139,92,246,0.25)'; } }}
        onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderBottomColor = 'transparent'; } }}
      >
        {localValue}
      </span>
    );
  }

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      {mode === 'editing' ? (
        <input
          ref={inputRef} type="text" value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
          style={{ width: '100%', display: 'block', background: 'rgba(139,92,246,0.12)', border: '1.5px solid #8b5cf6', borderRadius: 5, padding: '4px 8px', fontSize: 'inherit', fontWeight: 'inherit', color: '#e4e4e8', fontFamily: 'inherit', outline: 'none' }}
        />
      ) : (
        <span style={{ display: 'block', padding: '4px 8px', borderRadius: 5, background: 'rgba(139,92,246,0.12)', border: '1.5px solid #8b5cf6', fontSize: 'inherit', fontWeight: 'inherit' }}>
          {localValue}
        </span>
      )}

      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100 }}>
        <div style={{ backgroundColor: '#111115', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', overflow: 'hidden', minWidth: 300 }}>
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={mode !== 'editing' ? switchToEdit : undefined} style={{ flex: 1, padding: '7px 12px', border: 'none', cursor: 'pointer', backgroundColor: mode === 'editing' ? '#141416' : 'transparent', color: mode === 'editing' ? '#e4e4e8' : '#3f3f46', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderBottom: mode === 'editing' ? '2px solid #8b5cf6' : '2px solid transparent', transition: 'all 0.15s' }}>
              ✏️ Manual Edit
            </button>
            <button onClick={mode !== 'ai' ? switchToAI : undefined} style={{ flex: 1, padding: '7px 12px', border: 'none', cursor: 'pointer', backgroundColor: mode === 'ai' ? '#141416' : 'transparent', color: mode === 'ai' ? '#8b5cf6' : '#3f3f46', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderBottom: mode === 'ai' ? '2px solid #8b5cf6' : '2px solid transparent', transition: 'all 0.15s' }}>
              ✨ AI Rewrite
            </button>
          </div>
          <div style={{ padding: '8px 10px' }}>
            {mode === 'editing' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, color: '#71717b' }}>Type to change · Enter saves · Esc cancels</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={cancel} style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'transparent', color: '#71717b', fontSize: 10, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={save} style={{ padding: '3px 10px', borderRadius: 5, border: 'none', backgroundColor: '#8b5cf6', color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                </div>
              </div>
            )}
            {mode === 'ai' && (
              <div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input ref={aiInputRef} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleAISubmit(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
                    placeholder="e.g. make it more specific..."
                    disabled={aiLoading}
                    style={{ flex: 1, backgroundColor: '#1a1a1e', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 7, padding: '7px 10px', fontSize: 11, color: '#e4e4e8', fontFamily: 'inherit', outline: 'none' }}
                  />
                  <button onClick={handleAISubmit} disabled={!aiPrompt.trim() || aiLoading}
                    style={{ padding: '7px 16px', borderRadius: 7, border: 'none', backgroundColor: aiPrompt.trim() && !aiLoading ? '#8b5cf6' : '#222226', color: aiPrompt.trim() && !aiLoading ? '#fff' : '#3f3f46', fontSize: 11, fontWeight: 600, cursor: aiPrompt.trim() && !aiLoading ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', minWidth: 70, textAlign: 'center' as const }}>
                    {aiLoading ? 'Rewriting...' : 'Rewrite'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 9, color: '#3f3f46' }}>Enter to rewrite · Esc to cancel</span>
                  <button onClick={cancel} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'transparent', color: '#3f3f46', fontSize: 9, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </span>
  );
}

export default GenerateTOC;
